import { useCallback, useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";

import {
  usePatchPolicyByIdMutation,
  useGetPolicyByIdQuery,
  useDeletePolicyMutation,
} from "services/policy-api";
import { useGetRreValidationsQuery } from "services/rre-api";
import { setValidationState } from "reducers/main";

import { getPolicyForms } from "forms/rre";
import RREPageContainer from "./RREPageContainer";
import KaboodleForm from "components/Forms/KaboodleForm";
import { useDispatch, useSelector } from "utils/redux";
import { setIsLoading } from "reducers/main";
import { KaboodleToaster } from "components/common";

const RREPolicyForm = () => {
  const dispatch = useDispatch();
  const { policyId } = useParams<{ policyId?: string }>();
  const { rreId } = useParams<{ rreId?: string }>();
  const navigate = useNavigate();

  const {
    data: rrePolicy,
    isLoading,
    refetch: refetchPolicy,
  } = useGetPolicyByIdQuery(
    { id: policyId, rreId },
    { skip: !policyId || !rreId },
  );

  const { clearErrors, setError, reset } = useFormContext();
  useEffect(() => {
    dispatch(setIsLoading(isLoading));
    if (!isLoading && rrePolicy) {
      reset(rrePolicy);
    }
  }, [dispatch, isLoading]);

  const [patchRrePolicyById] = usePatchPolicyByIdMutation();
  const [deletePolicyById] = useDeletePolicyMutation();
  const constants = useSelector((state) => state.shared.constants);

  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      if (policyId) {
        try {
          let sanitizedValue = value;
          if (field === "plan_contact_phone") {
            sanitizedValue = (value as string).replace(
              new RegExp("-", "g"),
              "",
            );
          }
          await patchRrePolicyById({
            id: policyId,
            data: { [field]: sanitizedValue },
          }).unwrap();
          clearErrors(field);
        } catch (error) {
          console.error("Error saving RRE Policy:", error);
          KaboodleToaster.error("Failed to save RRE Policy data");
        }
      }
    },
    [policyId, patchRrePolicyById, clearErrors, refetchPolicy, setError],
  );

  const handleDelete = useCallback(async () => {
    if (policyId) {
      await deletePolicyById({ id: policyId }).unwrap();
      if (rreId) {
        navigate(`/section-111/rres/${rreId}/policies`);
      }
    }
  }, [policyId, deletePolicyById, rreId, navigate]);

  const { data: rreValidation } = useGetRreValidationsQuery(
    { id: rreId },
    { skip: !rreId },
  );
  useEffect(() => {
    if (rreValidation) {
      const validationData = Object.values(rreValidation).flat();
      const errors = validationData.filter((item) => item.level === "ERROR");
      const warnings = validationData.filter((item) => item.level === "WARN");
      dispatch(
        setValidationState({
          errors: errors,
          warnings: warnings,
        }),
      );
    }
  }, [dispatch, rreValidation]);

  const rrePolicyFormContent = useMemo(() => {
    const forms = getPolicyForms(constants);
    return (
      <KaboodleForm
        cards={forms[0].cards || []}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    );
  }, [constants, handleDelete, handleSave]);

  return <RREPageContainer>{rrePolicyFormContent}</RREPageContainer>;
};

export default RREPolicyForm;
